import { useState, useEffect } from 'react';
import { Form, message } from 'antd';
import { SOP_CONSTANTS } from '../../../constants/sopConstants';
import sopService from '../../../services/sopService';

export const useTemplateForm = (id, tenant, setHasExecutions, setSteps, setPreviewUrlsState) => {
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editForm] = Form.useForm();

    // 获取SOP模板详情
    const fetchTemplateDetail = async () => {
        // 如果是创建模式，不需要获取数据
        if (!id || id === 'new') {
            initCreateForm();
            return;
        }

        try {
            setLoading(true);
            // 从localStorage获取租户信息
            const tenantStr = localStorage.getItem('tenant');
            if (!tenantStr) {
                message.error('未获取到租户信息，请重新登录');
                setLoading(false);
                return;
            }
            const tenant = JSON.parse(tenantStr);
            const tenantId = tenant.id;

            // 检查模板是否有执行实例 - 仅做记录，不影响编辑功能
            try {
                const executionResult = await sopService.checkHasExecutions(id, tenantId);
                if (executionResult && executionResult.hasExecutions) {
                    setHasExecutions(true);
                    // 移除警告消息，允许编辑
                }
            } catch (error) {
                console.warn('检查执行实例失败:', error);
            }

            // 使用管理接口获取SOP详情（包含模板和步骤）
            const sopDetail = await sopService.getManagementDetail(id, tenantId);

            const templateDetail = sopDetail.template;
            const stepsData = sopDetail.steps || [];

            // 设置模板对象
            setTemplate(templateDetail);

            // ===== 处理步骤数据 =====
            const processedSteps = stepsData.map(step => {
                // 创建步骤的副本进行处理
                const processedStep = { ...step };

                // 保存原始的actionParams字符串，用于后续保存时使用
                if (processedStep.actionParams) {
                    processedStep.originalActionParams = processedStep.actionParams;
                }

                // 处理 actionParams - SopStepDTO.actionParams 在后端是 String 类型
                if (processedStep.actionParams) {
                    // 如果是字符串，尝试解析为对象
                    if (typeof processedStep.actionParams === 'string') {
                        try {
                            // 后端的 actionParams 应该是 JSON 字符串
                            processedStep.actionParams = JSON.parse(processedStep.actionParams);
                        } catch (e) {
                            console.warn(`步骤 ${step.id} actionParams无法解析:`, e);
                            // 解析失败时保留原始字符串
                        }
                    }
                } else {
                    // 如果actionParams不存在，初始化为空对象
                    processedStep.actionParams = {};
                }

                // 根据后端actionType反向转换为前端所需的type和细分actionType
                if (processedStep.actionType === 'SEND_MESSAGE') {
                    // 设置前端步骤类型
                    processedStep.type = 'MESSAGE';

                    // 初始化空的消息列表
                    processedStep.messageList = [];

                    // 处理文本消息
                    if (processedStep.actionParams.text) {
                        // 添加文本消息到消息列表
                        processedStep.messageList.push({
                            id: 'text-' + Date.now() + Math.random(),
                            type: 'TEXT',
                            params: {
                                content: processedStep.actionParams.text
                            }
                        });

                        // 默认actionType为TEXT
                        processedStep.actionType = 'TEXT';
                        processedStep.actionParams.content = processedStep.actionParams.text;
                    }

                    // 处理附件消息列表
                    if (processedStep.actionParams.attachments && Array.isArray(processedStep.actionParams.attachments)) {
                        processedStep.actionParams.attachments.forEach(attachment => {
                            if (attachment.msgtype === 'image' && attachment.image) {
                                // 添加图片消息
                                processedStep.messageList.push({
                                    id: 'image-' + Date.now() + Math.random(),
                                    type: 'IMAGE',
                                    params: {
                                        mediaId: attachment.image.media_id || attachment.image.pic_url,
                                        ossUrl: attachment.image.oss_url || ''
                                    }
                                });

                                // 如果没有文本消息，设置为图片类型
                                if (!processedStep.actionParams.text && processedStep.messageList.length === 1) {
                                    processedStep.actionType = 'IMAGE';
                                    processedStep.actionParams.mediaId = attachment.image.media_id || attachment.image.pic_url;
                                    processedStep.actionParams.mediaId_ossUrl = attachment.image.oss_url || '';
                                }
                            } else if (attachment.msgtype === 'link' && attachment.link) {
                                // 添加链接消息
                                processedStep.messageList.push({
                                    id: 'link-' + Date.now() + Math.random(),
                                    type: 'LINK',
                                    params: {
                                        title: attachment.link.title || '',
                                        description: attachment.link.desc || '',
                                        url: attachment.link.url || '',
                                        picUrl: attachment.link.picurl || '',
                                        picUrl_ossUrl: attachment.link.pic_oss_url || ''
                                    }
                                });

                                // 如果没有其他消息，设置为链接类型
                                if (!processedStep.actionParams.text && processedStep.messageList.length === 1) {
                                    processedStep.actionType = 'LINK';
                                    processedStep.actionParams.title = attachment.link.title || '';
                                    processedStep.actionParams.description = attachment.link.desc || '';
                                    processedStep.actionParams.url = attachment.link.url || '';
                                    processedStep.actionParams.picUrl = attachment.link.picurl || '';
                                    processedStep.actionParams.picUrl_ossUrl = attachment.link.pic_oss_url || '';
                                }
                            } else if (attachment.msgtype === 'miniprogram' && attachment.miniprogram) {
                                // 添加小程序消息
                                processedStep.messageList.push({
                                    id: 'miniprogram-' + Date.now() + Math.random(),
                                    type: 'MINIPROGRAM',
                                    params: {
                                        title: attachment.miniprogram.title || '',
                                        appid: attachment.miniprogram.appid || '',
                                        pagepath: attachment.miniprogram.page || '',
                                        thumbMediaId: attachment.miniprogram.pic_media_id || '',
                                        thumbMediaId_ossUrl: attachment.miniprogram.pic_oss_url || ''
                                    }
                                });

                                // 如果没有其他消息，设置为小程序类型
                                if (!processedStep.actionParams.text && processedStep.messageList.length === 1) {
                                    processedStep.actionType = 'MINIPROGRAM';
                                    processedStep.actionParams.title = attachment.miniprogram.title || '';
                                    processedStep.actionParams.appid = attachment.miniprogram.appid || '';
                                    processedStep.actionParams.pagepath = attachment.miniprogram.page || '';
                                    processedStep.actionParams.thumbMediaId = attachment.miniprogram.pic_media_id || '';
                                    processedStep.actionParams.thumbMediaId_ossUrl = attachment.miniprogram.pic_oss_url || '';
                                }
                            } else if (attachment.msgtype === 'video' && attachment.video) {
                                // 添加视频消息
                                processedStep.messageList.push({
                                    id: 'video-' + Date.now() + Math.random(),
                                    type: 'VIDEO',
                                    params: {
                                        mediaId: attachment.video.media_id || '',
                                        ossUrl: attachment.video.oss_url || ''
                                    }
                                });

                                // 如果没有其他消息，设置为视频类型
                                if (!processedStep.actionParams.text && processedStep.messageList.length === 1) {
                                    processedStep.actionType = 'VIDEO';
                                    processedStep.actionParams.mediaId = attachment.video.media_id || '';
                                    processedStep.actionParams.mediaId_ossUrl = attachment.video.oss_url || '';
                                }
                            } else if (attachment.msgtype === 'file' && attachment.file) {
                                // 添加文件消息
                                processedStep.messageList.push({
                                    id: 'file-' + Date.now() + Math.random(),
                                    type: 'FILE',
                                    params: {
                                        mediaId: attachment.file.media_id || '',
                                        ossUrl: attachment.file.oss_url || ''
                                    }
                                });

                                // 如果没有其他消息，设置为文件类型
                                if (!processedStep.actionParams.text && processedStep.messageList.length === 1) {
                                    processedStep.actionType = 'FILE';
                                    processedStep.actionParams.mediaId = attachment.file.media_id || '';
                                    processedStep.actionParams.mediaId_ossUrl = attachment.file.oss_url || '';
                                }
                            }
                        });
                    }

                    // 如果没有设置actionType，默认为TEXT
                    if (!processedStep.actionType) {
                        processedStep.actionType = 'TEXT';
                        processedStep.actionParams.content = '';
                    }

                } else if (processedStep.actionType === 'SEND_MOMENT') {
                    // 构建momentsList
                    processedStep.momentsList = [];

                    // 确保actionParams是对象形式
                    if (typeof processedStep.actionParams === 'string') {
                        try {
                            processedStep.actionParams = JSON.parse(processedStep.actionParams);
                        } catch (e) {
                            console.error('解析朋友圈actionParams失败:', e);
                            processedStep.actionParams = {
                                text: { content: "" },
                                attachments: []
                            };
                        }
                    }

                    // 处理文本内容
                    if (processedStep.actionParams.text && processedStep.actionParams.text.content) {
                        processedStep.momentsList.push({
                            id: 'moment-text-' + Date.now() + Math.random(),
                            type: 'MOMENT_TEXT',
                            params: {
                                content: processedStep.actionParams.text.content
                            }
                        });
                    }

                    // 收集图片附件，稍后统一处理（支持图片组）
                    const imageAttachments = [];
                    
                    // 处理附件内容
                    if (processedStep.actionParams.attachments && Array.isArray(processedStep.actionParams.attachments)) {
                        // 第一步：收集所有附件，区分图片和其他类型
                        processedStep.actionParams.attachments.forEach(attachment => {
                            if (attachment.msgtype === 'image' && attachment.image) {
                                // 收集图片附件
                                imageAttachments.push({
                                    mediaId: attachment.image.media_id || attachment.image.pic_url,
                                    ossUrl: attachment.image.oss_url || ''
                                });
                            } else if (attachment.msgtype === 'video' && attachment.video) {
                                processedStep.momentsList.push({
                                    id: 'moment-video-' + Date.now() + Math.random(),
                                    type: 'MOMENT_VIDEO',
                                    params: {
                                        mediaId: attachment.video.media_id || '',
                                        ossUrl: attachment.video.oss_url || ''
                                    }
                                });
                            } else if (attachment.msgtype === 'link' && attachment.link) {
                                processedStep.momentsList.push({
                                    id: 'moment-link-' + Date.now() + Math.random(),
                                    type: 'MOMENT_LINK',
                                    params: {
                                        title: attachment.link.title || '',
                                        description: attachment.link.desc || '',
                                        url: attachment.link.url || '',
                                        picUrl: attachment.link.picurl || '',
                                        picOssUrl: attachment.link.pic_oss_url || ''
                                    }
                                });
                            }
                        });
                        
                        // 第二步：处理图片附件 - 支持图片组
                        if (imageAttachments.length > 0) {
                            if (imageAttachments.length === 1) {
                                // 单张图片，保持原有的MOMENT_IMAGE类型
                                processedStep.momentsList.push({
                                    id: 'moment-image-' + Date.now() + Math.random(),
                                    type: 'MOMENT_IMAGE',
                                    params: {
                                        mediaId: imageAttachments[0].mediaId,
                                        ossUrl: imageAttachments[0].ossUrl
                                    }
                                });
                            } else {
                                // 多张图片，创建图片组
                                const groupId = 'moment-image-group-' + Date.now();
                                const imageGroup = {
                                    id: groupId,
                                    type: 'MOMENT_IMAGE_GROUP',
                                    params: {
                                        imageGroup: {
                                            mediaIds: [],
                                            ossUrls: {}
                                        }
                                    }
                                };
                                
                                // 添加所有图片到图片组
                                imageAttachments.forEach(img => {
                                    imageGroup.params.imageGroup.mediaIds.push(img.mediaId);
                                    imageGroup.params.imageGroup.ossUrls[img.mediaId] = img.ossUrl;
                                });
                                
                                // 将图片组添加到朋友圈列表
                                processedStep.momentsList.push(imageGroup);
                                
                                console.log('普通SOP：创建朋友圈图片组，包含', imageAttachments.length, '张图片:', imageGroup);
                            }
                        }
                    }
                    // 设置actionType保持一致
                    processedStep.actionType = 'SEND_MOMENT';
                } else if (processedStep.actionType === 'UPDATE_TAG') {
                    // 设置前端步骤类型
                    processedStep.type = 'TAG';

                    // 根据operation设置前端actionType
                    if (processedStep.actionParams.operation === 'ADD') {
                        processedStep.actionType = 'ADD_TAG';
                    } else {
                        processedStep.actionType = 'REMOVE_TAG';
                    }
                } else {
                    // 默认处理
                    console.warn(`步骤 ${step.id} 的actionType不受支持:`, processedStep.actionType);

                    // 设置默认值
                    processedStep.type = 'MESSAGE';
                    processedStep.actionType = 'TEXT';
                }

                return processedStep;
            });

            // 设置处理后的步骤
            setSteps(processedSteps);

            // ===== 设置预览URL =====
            if (setPreviewUrlsState) {
                const previewUrls = {};
                processedSteps.forEach(step => {
                    // 处理消息步骤的预览URL
                    if (step.messageList && Array.isArray(step.messageList)) {
                        step.messageList.forEach(msg => {
                            if (msg.params) {
                                // 图片、视频、文件的ossUrl
                                if (msg.params.ossUrl && msg.params.mediaId) {
                                    previewUrls[msg.params.mediaId] = msg.params.ossUrl;
                                }
                                // 链接的封面图ossUrl
                                if (msg.params.picUrl_ossUrl && msg.params.picUrl) {
                                    previewUrls[msg.params.picUrl] = msg.params.picUrl_ossUrl;
                                }
                                // 小程序的缩略图ossUrl
                                if (msg.params.thumbMediaId_ossUrl && msg.params.thumbMediaId) {
                                    previewUrls[msg.params.thumbMediaId] = msg.params.thumbMediaId_ossUrl;
                                }
                            }
                        });
                    }
                    
                    // 处理朋友圈步骤的预览URL
                    if (step.momentsList && Array.isArray(step.momentsList)) {
                        step.momentsList.forEach(moment => {
                            if (moment.params) {
                                // 朋友圈图片、视频的ossUrl
                                if (moment.params.ossUrl && moment.params.mediaId) {
                                    previewUrls[moment.params.mediaId] = moment.params.ossUrl;
                                }
                                // 朋友圈链接的封面图ossUrl
                                if (moment.params.picOssUrl && moment.params.picUrl) {
                                    previewUrls[moment.params.picUrl] = moment.params.picOssUrl;
                                }
                                // 朋友圈图片组的ossUrl
                                if (moment.type === 'MOMENT_IMAGE_GROUP' && moment.params.imageGroup) {
                                    const { mediaIds, ossUrls } = moment.params.imageGroup;
                                    if (mediaIds && ossUrls) {
                                        mediaIds.forEach(mediaId => {
                                            if (ossUrls[mediaId]) {
                                                previewUrls[mediaId] = ossUrls[mediaId];
                                            }
                                        });
                                    }
                                }
                            }
                        });
                    }
                });
                setPreviewUrlsState(previewUrls);
            }

            // ===== 设置表单值 =====
            // 基本表单字段 - 根据 SopTemplateDTO 字段设置
            const formValues = {
                name: templateDetail.name || '',
                code: templateDetail.code || '',
                description: templateDetail.description || '',
                targetType: templateDetail.targetType || SOP_CONSTANTS.TargetType.CUSTOMER,
                // 添加employeeIds字段设置
                employeeIds: templateDetail.employeeIds || [],
                // 添加targetIds字段设置（用于GROUP类型的SOP），确保转换为字符串数组以匹配Select组件
                targetIds: (templateDetail.targetIds || []).map(id => id.toString())
            };

            editForm.setFieldsValue(formValues);

            // 单独设置triggerRules，确保前后端结构匹配
            if (templateDetail.triggerRules && Array.isArray(templateDetail.triggerRules)) {
                // 确保每条规则都有必要的字段 - 根据 SopTriggerRuleDTO 字段
                const rules = templateDetail.triggerRules.map(rule => ({
                    tagId: rule.tagId,
                    operation: rule.operation || SOP_CONSTANTS.Operation.ADD,
                    excludeTagIds: rule.excludeTagIds || [], // 确保正确设置排除标签ID列表
                    ...rule // 保留其他字段
                }));

                editForm.setFieldValue('triggerRules', rules);
            } else {
                editForm.setFieldValue('triggerRules', []);
            }

        } catch (error) {
            console.error('获取SOP模板详情失败:', error);
            message.error('获取SOP模板详情失败: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    // 初始化创建表单
    const initCreateForm = () => {
        // 避免修改已有状态，创建全新对象
        const newTemplate = {
            name: '',
            code: 'SOP_' + new Date().getTime(), // 生成默认编码
            description: '',
            targetType: SOP_CONSTANTS.TargetType.CUSTOMER, // 默认目标类型为客户
            triggerRules: [], // 为触发规则初始化空数组
            status: SOP_CONSTANTS.TemplateStatus.DRAFT, // 默认为草稿状态
            employeeIds: [], // 初始化空的员工ID列表
            targetIds: [] // 初始化空的目标ID列表（用于GROUP类型）
        };

        setTemplate(newTemplate);

        // 设置表单默认值
        editForm.setFieldsValue({
            name: newTemplate.name,
            code: newTemplate.code,
            description: newTemplate.description,
            targetType: newTemplate.targetType,
            triggerRules: newTemplate.triggerRules,
            employeeIds: newTemplate.employeeIds, // 设置默认的空员工ID列表
            targetIds: newTemplate.targetIds // 设置默认的空目标ID列表（字符串数组）
        });

        // 重置步骤列表
        setSteps([]);
    };

    // 判断模板是否为激活状态
    const isTemplateActive = () => {
        return template && template.status === SOP_CONSTANTS.TemplateStatus.ACTIVE;
    };

    // 判断模板是否为激活状态或有执行实例
    const isTemplateDisabled = () => {
        return isTemplateActive(); // 只检查模板是否为激活状态，忽略执行实例
    };

    return {
        template,
        setTemplate,
        loading,
        setLoading,
        editForm,
        fetchTemplateDetail,
        initCreateForm,
        isTemplateActive,
        isTemplateDisabled
    };
}